// src/components/ClassRecordForm.js
import React, { useState, useEffect } from 'react';
import styles from './ClassRecordForm.module.css'; // 确保引入样式文件
// 引入 showNotification prop
// import { showNotification } from '../App'; // 假设从 App.js 导入，但实际是通过 prop 传递

// 导入 react-datepicker 组件和样式
import DatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

// 导入 date-fns 的相关函数
// 导入中文语言包
import zhCN from 'date-fns/locale/zh-CN';

// 注册中文语言包
registerLocale('zh-CN', zhCN);

// 定义课程内容选项 (保持不变)
const CONTENT_OPTIONS = [
    "健体",
    "古典",
    "传统",
    "模特",
    "比基尼",
    "形体",
    "展背",
    "底层逻辑",
    "其他" // 包含其他选项
];

// 定义费用预设值 (保持不变)
const DEFAULT_FEES = {
    '线上': '220',
    '线下': '350',
    '混合': '',
    '包月': '',
};

function ClassRecordForm({ editingRecord, onSave, onCancelEdit, isSubmitting, showNotification, allRecords, onLogout }) { // Receive onLogout prop
  const [recordTime, setRecordTime] = useState(null);
  const [person, setPerson] = useState('');
  const [method, setMethod] = useState('线上');
  const [fee, setFee] = useState('');
  const [remarks, setRemarks] = useState('');

  // 新增状态用于管理名字建议
  const [nameSuggestions, setNameSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // --- 修改课程内容相关的 state ---
  const [selectedContentOptions, setSelectedContentOptions] = useState([]); // 存储选中的选项字符串数组
  const [otherContentText, setOtherContentText] = useState(''); // 存储选择“其他”时输入的文本
  // --- 修改结束 ---

  const [editRecordId, setEditRecordId] = useState(null);
  // 新增 state 存储所有不重复的名字
  const [allUniqueNames, setAllUniqueNames] = useState([]);

  // 新增 useEffect 监听 allRecords 变化，提取所有不重复的名字并存储
  useEffect(() => {
      if (allRecords && allRecords.length > 0) {
          const uniqueNames = [...new Set(allRecords.map(record => record.person).filter(name => name))];
          uniqueNames.sort(); // 对名字进行排序
          setAllUniqueNames(uniqueNames); // 存储所有不重复的名字
          // console.log("Unique names extracted and stored:", uniqueNames); // 调试用
      } else {
          setAllUniqueNames([]); // 如果没有记录，清空列表
      }
  }, [allRecords]);


  // 使用 useEffect 监听 editingRecord prop 的变化 (初始化表单)
  useEffect(() => {
    if (editingRecord) {
      // 编辑模式
      setEditRecordId(editingRecord.id);
      try {
         setRecordTime(new Date(editingRecord.record_time));
      } catch (e) {
          console.error("Failed to parse date for editing:", editingRecord.record_time, e);
          setRecordTime(null);
      }

      setPerson(editingRecord.person || '');

      // --- 修改编辑模式下的课程内容填充逻辑 ---
      const existingContent = Array.isArray(editingRecord.content) ? editingRecord.content : (editingRecord.content ? [editingRecord.content] : []);

      // 临时存储标准选项
      const standardOptions = existingContent.filter(item => CONTENT_OPTIONS.includes(item) && item !== '其他');
      // 检查是否存在“其他”选项
      const hasOther = existingContent.includes('其他');
      // 提取“其他”后面的具体内容
      let otherText = '';
       if (hasOther) {
           const otherIndex = existingContent.indexOf('其他');
           // 假设“其他”后面的第一个元素是具体内容
           if (otherIndex !== -1 && otherIndex < existingContent.length - 1) {
               otherText = existingContent[otherIndex + 1] || '';
           }
           // 如果“其他”后面没有内容，或者数组只有['其他']，otherText 就是空
       }


      setSelectedContentOptions([...standardOptions, ...(hasOther ? ['其他'] : [])]); // 设置选中的标准选项和“其他”
      setOtherContentText(otherText); // 设置“其他”的具体文本
      // --- 课程内容填充结束 ---


      const recordMethod = editingRecord.method || '线上';
      setMethod(recordMethod);

      const recordFee = editingRecord.fee !== null && editingRecord.fee !== undefined ? editingRecord.fee.toString() : '';
      setFee(recordFee);

      setRemarks(editingRecord.remarks || '');

    } else {
      // 添加模式
      setEditRecordId(null);
      setRecordTime(new Date()); // 预填当前时间

      setPerson('');
      // 添加模式下，清空建议状态
      setNameSuggestions([]);
      setShowSuggestions(false);

      // --- 修改添加模式的课程内容初始化 ---
      setSelectedContentOptions([]); // 添加模式下，默认不选中任何课程内容
      setOtherContentText('');
      // --- 修改结束 ---

      const defaultMethod = '线上';
      setMethod(defaultMethod);
      setFee(DEFAULT_FEES[defaultMethod] || '');

      setRemarks('');
    }
  }, [editingRecord]);


  // useEffect 监听 method 变化并预填费用 (只在添加模式下)
  useEffect(() => {
       if (!editRecordId) { // 只在添加模式下响应 method 变化进行预填
           const defaultFeeForMethod = DEFAULT_FEES[method] || '';
           setFee(defaultFeeForMethod);
           // console.log(`Method changed to ${method}, prefilling fee to ${defaultFeeForMethod}`);
       }
   }, [method, editRecordId]); // 依赖项：method 和 editRecordId


  // --- 新增处理课程内容多选的 change 事件 ---
  const handleContentChange = (option) => {
      setSelectedContentOptions(prevOptions => {
          if (prevOptions.includes(option)) {
              // 如果已选中，则移除
              return prevOptions.filter(item => item !== option);
          } else {
              // 如果未选中，则添加
              // 可以选择在添加时保持 CONTENT_OPTIONS 的原始顺序，或者 simply add
              // 保持顺序更复杂，直接添加最简单
              return [...prevOptions, option];
          }
      });
  };

  // 检查某个选项是否被选中
  const isOptionSelected = (option) => {
      return selectedContentOptions.includes(option);
  };
  // --- 修改结束 ---

   // 处理名字输入框的变化
   const handlePersonChange = (e) => {
       const value = e.target.value;
       setPerson(value);

       // 根据输入过滤名字建议，使用存储的所有不重复的名字列表
       if (value) {
           const filteredSuggestions = allUniqueNames.filter(name =>
               name.toLowerCase().includes(value.toLowerCase())
           );
           setNameSuggestions(filteredSuggestions);
           setShowSuggestions(true); // 输入时显示建议列表
       } else {
           setNameSuggestions([]);
           setShowSuggestions(false); // 输入为空时隐藏建议列表
       }
   };

   // 处理选择建议
   const handleSelectSuggestion = (suggestion) => {
       setPerson(suggestion);
       setNameSuggestions([]); // 清空建议列表
       setShowSuggestions(false); // 隐藏建议列表
   };


  const handleSubmit = async (event) => {
    event.preventDefault();

    // 基本的前端必填项检查
    if (!recordTime || !person || !method) {
        showNotification('时间和人员、方式是必填项', 'error');
        return;
    }

    // --- 优化课程内容提交逻辑 ---
    let contentArrayToSend = [...selectedContentOptions]; // 从 state 获取选中的标准选项

    const trimmedOtherText = otherContentText.trim();

    // 如果“其他”被选中
    if (selectedContentOptions.includes('其他')) {
        // 检查“其他”文本框是否有内容
        if (trimmedOtherText) {
            // 将“其他”文本添加到数组中，紧跟在“其他”选项后面
            const otherIndex = contentArrayToSend.indexOf('其他');
            if (otherIndex !== -1) {
                contentArrayToSend.splice(otherIndex + 1, 0, trimmedOtherText);
            } else {
                 // 如果因为某种原因“其他”不在数组里（理论上不会），就直接加在末尾
                 contentArrayToSend.push(trimmedOtherText);
            }
        } else {
            // 如果选择了“其他”但文本框为空，阻止提交
            alert('请填写具体的课程内容');
            return;
        }
    }

    // 检查是否有任何内容被选中或填写（除了单独的“其他”选项）
    // 过滤掉单独的“其他”选项，只计算实际内容项
    const actualContentCount = contentArrayToSend.filter(item => item !== '其他').length;

    if (actualContentCount === 0 && !selectedContentOptions.includes('其他')) {
        // 如果没有选中任何标准选项，也没有选中“其他”
        alert('请选择课程内容');
        return;
    }
     if (selectedContentOptions.includes('其他') && trimmedOtherText === '') {
         // 选中了“其他”但文本框为空，上面已处理，这里可作为备用检查
          alert('请填写具体的课程内容');
          return;
      }

    // --- 课程内容提交逻辑优化结束 ---


    const recordDataToSave = {
      ...(editRecordId && { id: editRecordId }),
      record_time: recordTime,
      person: person,
      content: contentArrayToSend, // 发送构建好的内容数组
      method: method,
      fee: fee === '' ? null : parseFloat(fee),
      remarks: remarks === '' ? null : remarks,
    };

    try {
      await onSave(recordDataToSave);
    } catch (error) {
      console.error('表单提交处理失败:', error);
    }
  };

   const handleFeeChange = (e) => {
        setFee(e.target.value);
   };

  // 课程内容图片链接映射
  const imageUrlMap = {
      "健体": "https://img.liyao.sbs/file/1745858011152_健体3.jpg",
      "古典": "https://img.liyao.sbs/file/1745858347228_extended-image-1745858239239.png",
      "传统": "https://img.liyao.sbs/file/1745858549428_战争践踏.png",
      "模特": "https://placehold.co/200/transparent/orange?text=Men's Model&font=roboto",
      "比基尼": "https://placehold.co/200/transparent/orange?text=Bikini&font=roboto",
      "形体": "https://placehold.co/200/transparent/orange?text=Women's Physique&font=roboto",
      "展背": "https://placehold.co/200/transparent/orange?text=Lat Spread&font=roboto",
      "底层逻辑": "https://placehold.co/200/transparent/orange?text=Basic Logic&font=roboto",
      "其他": "https://placehold.co/200/transparent/orange?text=Other&font=roboto",
  };


  return (
    <form onSubmit={handleSubmit} className={styles.formContainer}>
      <div className={styles.headerContainer}> {/* New container for button and title */}
        <button type="button" onClick={onLogout} className={styles.logoutButton}>退出登录</button> {/* Logout button */}
        <h2>{editRecordId ? '编辑上课信息' : '录入上课信息'}</h2> {/* Title */}
      </div>

      {/* 时间字段 */}
      <div className={styles.formField}>
        <label htmlFor="recordTimePicker">时间:</label>
        <DatePicker
          id="recordTimePicker"
          selected={recordTime}
          onChange={(date) => setRecordTime(date)}
          showTimeSelect
          timeFormat="HH:mm"
          timeIntervals={15}
          dateFormat="yyyy年MM月dd日 HH:mm"
          placeholderText="请选择日期和时间"
          isClearable
          required
          className={styles.datePickerInput}
          locale="zh-CN"
        />
      </div>



      <div className={styles.formField}>
        <label htmlFor="person">名字:</label>
        {/* 新增一个 div 包裹输入框和建议列表 */}
        <div className={styles.personInputContainer} style={{ position: 'relative' }}>
            <input
                type="text"
                id="person"
                value={person}
                onChange={handlePersonChange} // 使用新的处理函数
                onFocus={() => {
                    if (allUniqueNames.length > 0) {
                        setNameSuggestions(allUniqueNames);
                        setShowSuggestions(true);
                    }
                }}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                required
            />
            {/* 清除按钮，仅在有内容时显示 */}
            {person && (
              <button
                type="button"
                onClick={() => setPerson('')}
                style={{
                  position: 'absolute',
                  right: '6px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none', // 半透明红色背景
                  border: 'none', // 半透明红色边框
                  borderRadius: '50%', // 圆形
                  cursor: 'pointer',
                  fontSize: '16px',
                  color: '#d00',
                  padding: '2px 6px',
                  lineHeight: 1,
                  transition: 'background 0.2s',
                }}
              >
                ×
              </button>
            )}
            {/* 名字建议列表 */}
            {showSuggestions && nameSuggestions.length > 0 && (
                <ul className={styles.suggestionsList}>
                    {nameSuggestions.map((suggestion, index) => (
                        <li key={index} onClick={() => handleSelectSuggestion(suggestion)}>
                            {suggestion}
                        </li>
                    ))}
                </ul>
            )}
        </div>
      </div>
      
       {/* --- 修改课程内容字段为图片多选框 --- */}
      <div className={styles.formField}>
        <label>课程内容:</label> {/* Label for the group */}
        <div className={styles.contentOptionsContainer}> {/* 容器用于布局 */}
          {CONTENT_OPTIONS.map(option => (
            <label
                key={option} // 确保每个选项有一个唯一的 key
                className={`${styles.contentOptionLabel} ${isOptionSelected(option) ? styles.selected : ''}`} // 根据是否选中添加样式类
            >
              {/* 隐藏的原生复选框 */}
              <input
                type="checkbox"
                value={option}
                checked={isOptionSelected(option)} // 根据 state 判断是否勾选
                onChange={() => handleContentChange(option)} // 绑定 change 事件
                className={styles.hiddenCheckbox} // 隐藏复选框
              />
              {/* 显示的图片 */}
              <img
                src={imageUrlMap[option]} // 使用映射对象获取图片链接
                alt={`选项: ${option}`} // 务必提供有意义的 alt 文本
                className={styles.contentOptionImage}
              />
              {/* 视觉上隐藏的文本 */}
              <span className="visually-hidden">{option}</span> {/* 使用 visually-hidden 类隐藏 */}
            </label>
          ))}
        </div>

        {/* 如果“其他”被选中，显示额外的文本输入框 */}
        {isOptionSelected('其他') && ( // 根据“其他”是否在 selectedContentOptions 中判断显示
            <input
                type="text"
                value={otherContentText}
                onChange={(e) => setOtherContentText(e.target.value)}
                placeholder="请填写具体的课程内容"
                required // 选择了“其他”后，这个文本框变成必填
                className={styles.otherContentInput}
                style={{ marginTop: '10px' }}
            />
        )}
      </div>
      {/* --- 修改结束 --- */}

      {/* 方式和费用容器 */}
      <div className={styles.methodFeeContainer}>
        <div className={styles.formField}>
          <label>方式:</label>
          <div className={styles.methodOptionsContainer}>
            {['线上', '线下', '混合', '包月'].map((option) => (
              <label
                key={option}
                className={`${styles.methodOptionLabel} ${method === option ? styles.selected : ''}`}
              >
                <input
                  type="radio"
                  name="method"
                  value={option}
                  checked={method === option}
                  onChange={(e) => setMethod(e.target.value)}
                  className={styles.hiddenRadio}
                />
                {option}
              </label>
            ))}
          </div>
        </div>

        {/* 费用字段 */}
        <div className={styles.formField}>
          <label htmlFor="fee">费用:</label>
          <input
              type="number"
              id="fee"
              step="1"
              value={fee}
              onChange={handleFeeChange}
          />
        </div>
      </div>

      <div className={styles.formField}>
        <label htmlFor="remarks">备注:</label>
        <div style={{ position: 'relative' }}>
          <textarea
            id="remarks"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            rows="2"
            style={{ width: '100%', boxSizing: 'border-box' }}
          ></textarea>
          {remarks && (
            <button
              type="button"
              onClick={() => setRemarks('')}
              aria-label="清除备注"
              style={{
                position: 'absolute',
                right: '6px',
                top: 'calc(50% - 3px)',
                transform: 'translateY(-50%)',
                background: 'none', // 半透明红色背景
                border: 'none', // 半透明红色边框
                cursor: 'pointer',
                fontSize: '16px',
                color: '#d00',
                padding: '2px 6px',
                lineHeight: 1,
                transition: 'background 0.2s',
              }}
            >
              ×
            </button>
          )}
        </div>
      </div>

      {/* 按钮区域 */}
      <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
        <button type="submit" className={styles.submitButton} disabled={isSubmitting}>
          {isSubmitting ? (editRecordId ? '更新中...' : '添加中...') : (editRecordId ? '更新记录' : '添加记录')}
        </button>

        {editRecordId && (
          <button
            type="button"
            onClick={onCancelEdit}
            className={styles.cancelButton}
            disabled={isSubmitting}
          >
            取消
          </button>
        )}
      </div>
    </form>
  );
}

export default ClassRecordForm;
